import { api } from "../base";
import type { IMarkupTier } from "@pulse-ui/codegen-v2/generated_ts/markup_tier";
import type { IClstAccountTier } from "@pulse-ui/codegen-v2/generated_ts/clst_account_tier";
import type { IPulseApiResponse } from "@pulse-ui/codegen-v2/generated_ts/api";
import type {
  IBookSpotTradeRequest,
  IBulkBookJobAccepted,
  IBulkBookJobStatusResponse,
  IBulkBookSpotTradeRequest,
  IFixExecutionReport,
  ITalosContractDealRequest,
  ITradeData,
} from "@pulse-ui/codegen-v2/generated_ts/venues/talos";

// ── Types ───────────────────────────────────────────────────────────────────

export type {
  IMarkupTier,
  IClstAccountTier,
  IBookSpotTradeRequest,
  IBulkBookJobAccepted,
  IBulkBookJobStatusResponse,
  IBulkBookSpotTradeRequest,
  IFixExecutionReport,
  ITalosContractDealRequest,
  ITradeData,
};

export {
  TalosOptionType,
  TalosPositionIndicator,
  TalosSide,
  BulkBookJobStatus,
  BulkBookRowStatus,
  type IBulkBookAssetDelta,
  type IBulkBookPreflightFailure,
  type IBulkBookPreflightResponse,
  type IBulkBookSpotTradeResultItem,
  type IBulkBookValidationFailure,
  type IBulkBookValidationResponse,
} from "@pulse-ui/codegen-v2/generated_ts/venues/talos";

export type TradeSide = "Buy" | "Sell";

export interface GetTradesParams {
  startDate?: string;
  endDate?: string;
  symbol?: string;
  markets?: string[];
  subAccounts?: string[];
  limit?: number;
}

// trading-ops-server response wrapper for POST /book-trade?asset_type=options.
// No YAML schema exists for this shape yet — follow-up to add to messages-public.
export interface DealRecord {
  Timestamp: string;
  User: string;
  DealID: string;
  Status: string;
  DisplaySymbol: string;
  Text: string;
}

export interface BookOptionsTradeResponse {
  type: string;
  ts: string;
  data: DealRecord[];
}

export interface MarkupTierOption {
  code: string;
  display_name: string;
}

// ── Endpoints ───────────────────────────────────────────────────────────────

const tradeEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    // ── Markup Tiers ──────────────────────────────────────────────────

    /** Fetches all markup tiers. */
    getMarkupTiers: build.query<IMarkupTier[], void>({
      query: () => ({ url: "/api/trade/v1/markup-tier" }),
      providesTags: ["MarkupTiers"],
    }),

    /** Creates a markup tier. */
    createMarkupTier: build.mutation<void, Omit<IMarkupTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/markup-tier",
        method: "POST",
        body: tier,
      }),
      invalidatesTags: ["MarkupTiers"],
    }),

    /** Updates a markup tier. */
    updateMarkupTier: build.mutation<void, Omit<IMarkupTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/markup-tier",
        method: "PUT",
        body: tier,
      }),
      invalidatesTags: ["MarkupTiers"],
    }),

    /** Deletes a markup tier by code. */
    deleteMarkupTier: build.mutation<void, string>({
      query: (tierCode) => ({
        url: "/api/trade/v1/markup-tier",
        method: "DELETE",
        params: { tier_code: tierCode },
      }),
      invalidatesTags: ["MarkupTiers"],
    }),

    // ── Account Tiers ─────────────────────────────────────────────────

    /** Fetches all account-tier assignments. */
    getAccountTiers: build.query<IClstAccountTier[], void>({
      query: () => ({ url: "/api/trade/v1/clst-account-tier" }),
      providesTags: ["AccountTiers"],
    }),

    /** Fetches markup tier options (code + display_name) for account-tier dropdowns. */
    getMarkupTierOptions: build.query<MarkupTierOption[], void>({
      query: () => ({ url: "/api/trade/v1/markup-tier" }),
      transformResponse: (response: IMarkupTier[]) =>
        (response ?? []).map((t) => ({
          code: t.code,
          display_name: t.display_name,
        })),
      providesTags: ["MarkupTiers"],
    }),

    /** Creates an account-tier assignment. */
    createAccountTier: build.mutation<void, Omit<IClstAccountTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/clst-account-tier",
        method: "POST",
        body: tier,
      }),
      invalidatesTags: ["AccountTiers"],
    }),

    /** Updates an account-tier assignment. */
    updateAccountTier: build.mutation<void, Omit<IClstAccountTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/clst-account-tier",
        method: "PUT",
        body: tier,
      }),
      invalidatesTags: ["AccountTiers"],
    }),

    /** Deletes an account-tier assignment. */
    deleteAccountTier: build.mutation<void, string>({
      query: (clstAccount) => ({
        url: "/api/trade/v1/clst-account-tier",
        method: "DELETE",
        params: { clst_account: clstAccount },
      }),
      invalidatesTags: ["AccountTiers"],
    }),

    /** Books a spot trade via trading-ops. */
    bookSpotTrade: build.mutation<IFixExecutionReport, IBookSpotTradeRequest>({
      query: (body) => ({
        url: "/api/trading-ops/v1/book-trade",
        method: "POST",
        params: { asset_type: "spot" },
        body,
      }),
    }),

    /** Books an options trade via trading-ops. The wire body extends
     * ITalosContractDealRequest with a `comments` field that trading-ops uses
     * to inject settlement_date overrides; not declared in the YAML schema. */
    bookOptionsTrade: build.mutation<
      BookOptionsTradeResponse,
      ITalosContractDealRequest & { comments: string }
    >({
      query: (body) => ({
        url: "/api/trading-ops/v1/book-trade",
        method: "POST",
        params: { asset_type: "options" },
        body,
      }),
    }),

    /** Cancels a customer trade by trade ID. */
    cancelCustomerTrade: build.mutation<IPulseApiResponse, string>({
      query: (tradeId) => ({
        url: `/api/trading-ops/v1/customer-trades/${tradeId}`,
        method: "DELETE",
      }),
    }),

    /**
     * Submits a bulk-book batch. Returns 202 + a job handle once the batch
     * passes synchronous validation and aggregate credit pre-flight; poll
     * `getBulkJobStatus` for execution progress. A 400 carries either a
     * `IBulkBookValidationResponse` or `IBulkBookPreflightResponse` in `error.data`.
     */
    bulkBookTrades: build.mutation<IBulkBookJobAccepted, IBulkBookSpotTradeRequest>({
      query: (body) => ({
        url: "/api/trading-ops/v1/book-trades/bulk",
        method: "POST",
        body,
      }),
    }),

    /** Polls the status of an async bulk-book job. */
    getBulkJobStatus: build.query<IBulkBookJobStatusResponse, string>({
      query: (jobId) => ({
        url: `/api/trading-ops/v1/bulk-jobs/${jobId}`,
      }),
    }),

    /** Fetches recent trades from trading-ops-server. */
    getTrades: build.query<ITradeData[], GetTradesParams>({
      query: ({ startDate, endDate, symbol, markets, subAccounts, limit }) => ({
        url: "/api/trading-ops/v1/trades",
        params: {
          ...(startDate && { start_date: startDate }),
          ...(endDate && { end_date: endDate }),
          ...(symbol && { symbol }),
          ...(markets && markets.length > 0 && { markets: markets.join(",") }),
          ...(subAccounts &&
            subAccounts.length > 0 && { sub_accounts: subAccounts.join(",") }),
          ...(limit !== undefined && { limit }),
        },
      }),
      transformResponse: (response: ITradeData[] | { data: ITradeData[] }) =>
        Array.isArray(response) ? response : (response.data ?? []),
      providesTags: ["Trades"],
    }),
  }),
});

export const {
  useGetMarkupTiersQuery,
  useCreateMarkupTierMutation,
  useUpdateMarkupTierMutation,
  useDeleteMarkupTierMutation,
  useGetAccountTiersQuery,
  useGetMarkupTierOptionsQuery,
  useCreateAccountTierMutation,
  useUpdateAccountTierMutation,
  useDeleteAccountTierMutation,
  useCancelCustomerTradeMutation,
  useBookSpotTradeMutation,
  useBookOptionsTradeMutation,
  useBulkBookTradesMutation,
  useGetBulkJobStatusQuery,
  useGetTradesQuery,
  useLazyGetTradesQuery,
} = tradeEndpoints;
